/**
 * Price Slider filter component.
 *
 * Dual-handle range slider for price filtering.
 * Uses react-slider for smooth interaction.
 */

'use client';

import { Icon } from '@/components/ui/Icon';
import { useSearchParams } from '@/lib/algolia/hooks/useSearchParams';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import ReactSlider from 'react-slider';
import styles from './PriceSliderFilter.module.scss';

interface PriceSliderFilterProps {
  minPrice?: number;
  maxPrice?: number;
  /** Absolute min available (from Algolia facet stats) */
  absoluteMin?: number;
  /** Absolute max available (from Algolia facet stats) */
  absoluteMax?: number;
  /** ISO currency code shown after the amount (e.g. "USD") */
  currency?: string;
}

// Default price range if not provided
const DEFAULT_MIN = 0;
const DEFAULT_MAX = 50000;
const STEP = 100;

export function PriceSliderFilter({
  minPrice,
  maxPrice,
  absoluteMin = DEFAULT_MIN,
  absoluteMax = DEFAULT_MAX,
  currency = 'USD',
}: PriceSliderFilterProps) {
  const { setParams } = useSearchParams();
  const t = useTranslations('plp');

  // Local state for slider dragging values
  const [localRange, setLocalRange] = useState<[number, number] | null>(null);
  const [isExpanded, setIsExpanded] = useState(!!minPrice || !!maxPrice);

  // The effective range is either the local dragging state or derived from props
  const effectiveRange = useMemo<[number, number]>(() => {
    if (localRange) return localRange;
    return [minPrice ?? absoluteMin, maxPrice ?? absoluteMax];
  }, [localRange, minPrice, maxPrice, absoluteMin, absoluteMax]);

  const handleSliderChange = useCallback((newRange: [number, number]) => {
    setLocalRange(newRange);
  }, []);

  const handleSliderAfterChange = useCallback(
    (newRange: [number, number]) => {
      const [min, max] = newRange;

      // Clear local state - will use URL params now
      setLocalRange(null);

      // Only set params if different from absolute bounds
      setParams({
        'price-min': min > absoluteMin ? min : null,
        'price-max': max < absoluteMax ? max : null,
        page: 1,
      });
    },
    [setParams, absoluteMin, absoluteMax]
  );

  const _hasValue = minPrice !== undefined || maxPrice !== undefined;

  // Calculate active filter count: 1 if either min or max is set, 2 if both are set
  const activeCount = useMemo(() => {
    const hasMin = minPrice !== undefined && minPrice > absoluteMin;
    const hasMax = maxPrice !== undefined && maxPrice < absoluteMax;
    if (hasMin && hasMax) return 2;
    if (hasMin || hasMax) return 1;
    return 0;
  }, [minPrice, maxPrice, absoluteMin, absoluteMax]);

  // Format as "1,000 USD" (amount then currency code), matching the design.
  // TODO: derive `currency` from the active channel for non-USD markets.
  const formatPrice = (value: number) => {
    const amount = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    return `${amount} ${currency}`;
  };

  return (
    <div className={`${styles.priceSlider} ${isExpanded ? styles.expanded : ''}`}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className={styles.name}>
          {t('facets.price')}
          {activeCount > 0 && <span className={styles.activeCount}>{activeCount}</span>}
        </span>
        <Icon
          name="chevron-down"
          size={20}
          className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
        />
      </button>

      {isExpanded && (
        <div className={styles.content}>
          <div className={styles.sliderContainer}>
            <ReactSlider
              className={styles.slider}
              thumbClassName={styles.thumb}
              trackClassName={styles.track}
              min={absoluteMin}
              max={absoluteMax}
              step={STEP}
              value={effectiveRange}
              onChange={handleSliderChange}
              onAfterChange={handleSliderAfterChange}
              pearling
              minDistance={STEP}
              ariaLabel={['Minimum price', 'Maximum price']}
              renderThumb={(props, state) => (
                <div {...props} key={props.key}>
                  <span className={styles.thumbLabel}>{formatPrice(state.valueNow)}</span>
                </div>
              )}
            />
          </div>

          <div className={styles.rangeLabels}>
            <span>{formatPrice(absoluteMin)}</span>
            <span>{formatPrice(absoluteMax)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
